import React, { useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import ChannelLabel, { ChannelIconType } from '@op/components/ChannelLabel';
import useIntls from '@opm/hooks/useIntls';
import SvgIcon from '@home/components/SvgIcon';
import { DefaultImg } from '@op/constants/defaultImg';
import RenderImg from '@op/components/RenderImg';
import TextClamp from '@opm/components/TextClamp';
import BubbleCommon from '@op/components/BubbleCommon';
import {
  getMemeTitle,
  getMemeContent,
  getMemeWhyHot,
  getMemeRegionCode,
} from '@opm/utils/language';
import { getMemeTimeInfo } from '@opm/pages/Memes/utils';
import { DBO_TM, DBO_U } from '@op/utils/format';
import moment from 'moment';
import IconVideoPlay from '@opm/assets/svg/icon-video-play.svg';
import MemeTagsCell from '@opm/pages/Memes/components/MemeTagsCell';
import './index.scss';
import RelatedHashtags from './RelatedHashtags';

interface IMemeInfoProps {
  data: any;
  loading: boolean;
  compact?: boolean;
  onGoToDetail?: () => void;
}

// ==================== 子组件 ====================

/** 渠道标签列表 */
function ChannelLabels({ channels }: { channels: string[] }) {
  return (
    <>
      {channels?.map((ch: string) => (
        <ChannelLabel
          key={ch}
          channel_name={ch as ChannelIconType}
          iconStyle={{ width: 16, height: 16 }}
        />
      ))}
    </>
  );
}

/** 视频播放器 / 封面 */
function VideoPlayer({ compact, data }: { compact: boolean; data: any }) {
  const containerClass = compact
    ? 'dbom-meme-detail-player--compact'
    : 'dbom-meme-detail-player';

  return (
    <div className={containerClass}>
      {data?.demo_video_url ? (
        <video
          className="dbom-meme-detail-video"
          src={data.demo_video_url}
          poster={data?.raw_cover}
          controls
          preload="metadata"
        />
      ) : (
        <>
          <RenderImg
            coverImage={data?.raw_cover}
            defaultImage={DefaultImg.VIDEO}
            className="dbom-meme-detail-poster"
            style={
              compact
                ? { backgroundRepeat: 'no-repeat', backgroundSize: 'contain' }
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              DBO_U.open(data?.raw_url);
            }}
          />
          <img src={IconVideoPlay} className="dbom-meme-detail-poster-icon" />
        </>
      )}
    </div>
  );
}

/** AI 卡片 */
function AiCard({
  compact,
  title,
  text,
  extraClass,
}: {
  compact: boolean;
  title: string;
  text: string;
  extraClass?: string;
}) {
  const className = compact
    ? 'dbom-meme-detail-ai-card dbom-meme-detail-ai-card'
    : `dbom-meme-detail-ai-card ${extraClass || ''}`;

  return (
    <div className={className}>
      <div className="dbom-meme-detail-ai-card-title">
        <img src={DefaultImg.AI} className="dbom-meme-ai-icon" />
        {title}
      </div>
      {compact ? (
        <div className="dbom-meme-detail-ai-card-text">{text}</div>
      ) : (
        <TextClamp
          rowNum={3}
          text={text}
          className="dbom-meme-detail-ai-card-text"
        />
      )}
    </div>
  );
}

// ==================== 主组件 ====================

export default function MemeInfo(props: IMemeInfoProps) {
  const { data, loading, compact, onGoToDetail } = props;
  const { intl, isEn } = useIntls();

  const { title, aiInterpretation, whyHot, region } = useMemo(() => {
    const language = isEn ? 'en' : 'zh';
    return {
      title: getMemeTitle(language, data),
      aiInterpretation: getMemeContent(language, data),
      whyHot: getMemeWhyHot(language, data),
      region: getMemeRegionCode(language, data),
    };
  }, [isEn, data]);

  const timeInfo = useMemo(
    () =>
      getMemeTimeInfo(
        data?.channels,
        data?.hottest_time,
        data?.min_release_time,
      ),
    [data?.channels, data?.hottest_time, data?.min_release_time],
  );

  // const daysActiveText = useMemo(() => { 
  //   const minTime = data?.min_release_time;
  //   const maxTime = data?.max_release_time;
  //   if (!minTime || !maxTime) return '';
  //   const days = Math.max(1, moment(maxTime).diff(moment(minTime), 'days'));
  //   return intl(
  //     { id: 'More than {days} days, till {time}' },
  //     { days, time: DBO_TM.sFormat(maxTime) },
  //   );
  // }, [data, intl]);

  // 元数据项的 CSS 类名
  const cls = (compactMode: boolean) => ({
    item: compactMode
      ? 'dbom-meme-detail-meta-item--compact'
      : 'dbom-meme-detail-meta-item',
    label: compactMode
      ? 'dbom-meme-detail-meta-label--compact'
      : 'dbom-meme-detail-meta-label',
    value: compactMode
      ? 'dbom-meme-detail-meta-value--compact'
      : 'dbom-meme-detail-meta-value',
  });

  // 标签渲染（compact 用 BubbleCommon, full 用 BubbleCommon 包装部分）
  const renderLabel = (
    compactMode: boolean,
    text: string,
    useBubble = false,
  ) => {
    if (useBubble && !compactMode) {
      return (
        <BubbleCommon
          showIcon={false}
          tipsMap={null}
          childClassName={cls(compactMode).label}
        >
          {text}
        </BubbleCommon>
      );
    }
    return <span className={cls(compactMode).label}>{text}</span>;
  };

  /** 渲染元数据项列表 */
  const renderMetaItems = (compactMode: boolean) => {
    const c = cls(compactMode);

    return (
      <>
        {/* 时间展示：bilibili 不展示，tiktok 展示 Hot Time，其他展示 First Seen */}
        {timeInfo && (
          <div className={c.item}>
            {renderLabel(compactMode, intl({ id: timeInfo.labelKey }))}
            <span className={c.value}>
              {(() => {
                if (!timeInfo.time) return '-';
                if (timeInfo.withPrefix) {
                  return intl(
                    { id: 'Before {time}' },
                    { time: DBO_TM.sFormat(timeInfo.time) },
                  );
                }
                return DBO_TM.sFormat(timeInfo.time);
              })()}
            </span>
          </div>
        )}

        {/* Channel */}
        <div className={c.item}>
          <span className={c.label}>{intl('Channel')}</span>
          <span className={c.value}>
            <ChannelLabels channels={data?.channels} />
          </span>
        </div>

        {/* Region */}
        {(compactMode ? region : true) && (
          <div className={c.item}>
            <span className={c.label}>{intl('Region')}</span>
            <span className={c.value}>
              {compactMode ? region : region || '-'}
            </span>
          </div>
        )}

        {/* Type & Elements */}
        <div className={c.item}>
          <span className={c.label}>{intl('Type & Elements')}</span>
          {compactMode ? (
            <span className={`${c.value} dbom-meme-detail-meta-tags`}>
              <MemeTagsCell record={data} isEn={isEn} />
            </span>
          ) : (
            <div className="dbom-meme-detail-tags">
              <MemeTagsCell record={data} isEn={isEn} />
            </div>
          )}
        </div>

        {/* Days Active */}
        {/* {daysActiveText && (
          <div className={c.item}>
            {renderLabel(compactMode, intl('Days Active'), true)}
            <span className={c.value}>{daysActiveText}</span>
          </div>
        )} */}
      </>
    );
  };

  // ==================== Compact 模式 ====================

  if (compact) {
    return (
      <>
        <div className="dbom-meme-detail-info--compact">
          {/* Basics header with Details link */}
          <div className="dbom-meme-detail-compact-header">
            <span className="dbom-meme-detail-compact-header__title">
              {intl('Basics')}
            </span>
            {onGoToDetail && (
              <span
                className="dbom-meme-detail-compact-header__link"
                onClick={onGoToDetail}
              >
                <SvgIcon
                  type="view-details-icon"
                  className="dbom-meme-detail-compact-header__link-icon"
                />
                <span>{intl('Details')}</span>
              </span>
            )}
          </div>

          {/* Video Player */}
          <VideoPlayer compact data={data} />

          {/* Metadata */}
          <div className="dbom-meme-detail-meta--compact">
            {renderMetaItems(true)}
          </div>
        </div>

        {/* Related Hashtags */}
        {data?.tags?.length > 0 && (
          <div className="dbom-meme-detail-hashtags--compact">
            <span className="dbom-meme-detail-compact-header__title">
              {intl('Related Hashtags')}
            </span>
            <RelatedHashtags
              tags={data.tags}
              defaultVisibleCount={10}
              showShareIcon={false}
              commonBubbleProps={{ placement: 'left' }}
            />
          </div>
        )}

        {/* AI Cards — stacked in compact mode */}
        {aiInterpretation && (
          <div className="dbom-meme-detail-ai-cards--compact">
            <AiCard
              compact
              title={intl('AI Meme Interpretation')}
              text={aiInterpretation}
            />
          </div>
        )}
        {whyHot && (
          <div className="dbom-meme-detail-ai-cards--compact">
            <AiCard compact title={intl('Why Hot')} text={whyHot} />
          </div>
        )}
      </>
    );
  }

  // ==================== 完整模式 ====================

  return (
    <CardCommon
      loading={loading}
      loadingHeight={280}
      emptyHeight={280}
      className="dbom-meme-detail-info"
      empty={!data || Object.keys(data).length === 0}
      hasEmptyImg={false}
      title={title}
    >
      <div className="dbom-meme-detail-info-content">
        {/* <div className="dbom-meme-detail-al-brainstorm">
          <span>AI Brainstorming</span>
        </div> */}
        {/* 左侧视频播放器 */}
        <VideoPlayer compact={false} data={data} />

        {/* 右侧信息区 */}
        <div className="dbom-meme-detail-right">
          {/* Metadata */}
          <div className="dbom-meme-detail-meta">
            {renderMetaItems(false)}

            {/* Related Hashtags */}
            <div className="dbom-meme-detail-meta-item --vertical-top">
              <span className="dbom-meme-detail-meta-label">
                {intl('Related Hashtags')}
              </span>
              <RelatedHashtags
                tags={data?.tags || []}
                defaultVisibleCount={10}
                showShareIcon={true}
                commonBubbleProps={{ placement: 'left' }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* AI Cards — 并排 */}
      <CardCommon className="dbom-meme-detail-ai-cards" contentCard>
        <AiCard
          compact={false}
          title={intl('AI Meme Interpretation')}
          text={aiInterpretation}
          extraClass="dbom-meme-detail-ai-card--blue"
        />
        {!whyHot && (
          <AiCard
            compact={false}
            title={intl('Why Hot')}
            text={whyHot}
            extraClass="dbom-meme-detail-ai-card--pink"
          />
        )}
      </CardCommon>
    </CardCommon>
  );
}
